import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { gradeApi } from '../../../api.js';
import { useAuth } from '../../../auth/AuthContext';
import { Loading } from '../../Loading';
import { DueTimeDisplayTable } from '../../DueTimeDisplayTable';
import Table from '../../Table.js';
import TeacherStudentUpload from './TeacherStudentUpload';
import HierarchicalQuestionView from '../HierarchicalQuestionView';
import { toast } from 'react-toastify';

function TGrade({
  isLoading,
  pageNumber,
  submissionsError,
  submissions,
  itemsPerPage,
  assignment,
  reviewMode,
  setReviewMode,
  updateSearchParams,
  setUpdateTrigger,
  isReleaseMode,
  selectedStudents = [], // Provide default empty array
  handleStudentSelectionChange,
  releaseOptions = {
    submissionPdf: true,
    grades: true,
    feedback: true
  },
  handleReleaseOptionChange
}) {
  const { user } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const [activeKebabMenu, setActiveKebabMenu] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [regrades, setRegrades] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [showSubQuestions, setShowSubQuestions] = useState(false);
  const [animatingToSubQuestions, setAnimatingToSubQuestions] = useState(false);
  const [examPaper, setExamPaper] = useState([]);
  const [showMoreViews, setShowMoreViews] = useState(false);
  const [allowRegrades, setAllowRegrades] = useState(assignment?.allow_regrade || false);

  // Get sort parameters from URL query string
  const queryParams = new URLSearchParams(location.search);
  const initialSortKey = queryParams.get('sortKey') || '';
  const initialSortDirection = queryParams.get('sortDir') || 'asc';

  // State for sorting in TGrade component
  const [sortConfig, setSortConfig] = useState({
    key: initialSortKey,
    direction: initialSortDirection
  });

  // Keep options panel open when non-default view is selected
  useEffect(() => {
    if (reviewMode !== 'Student') {
      setShowMoreViews(true);
    }
  }, [reviewMode]);

  useEffect(() => {
    console.log(submissions);
    getRegrades();
  }, []);

  useEffect(() => {
    if (submissionsError) {
      setErrorMessage(submissionsError);
    }
  }, [submissionsError]);

  const getQuestions = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment.id}/questions/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      setExamPaper(response.data.questions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setErrorMessage('Failed to fetch questions');
    }
  };

  useEffect(() => {
    if (assignment?.id) {
      getQuestions();
    }
  }, [assignment]);

  const handleStartGrading = subQuestionId => {
    navigate(`group/0`, {
      state: {
        students: (submissions?.entities ?? []).slice(0, 10),
        allStudents: submissions?.entities ?? [],
        initialSubQuestion: subQuestionId
      }
    });
  };

  const getRegrades = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/regrade-requests/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      const regradesForTeacher = response.data;
      console.log(regradesForTeacher);
      setRegrades(regradesForTeacher);
    } catch (error) {
      console.log(error);
    }
  };

  const hasRegrade = submission => {
    for (var i = 0; i < regrades.length; i++) {
      if (regrades[i].id === submission.id) {
        return true;
      }
    }
    return false;
  };

  const handleUploadClick = studentId => {
    setSelectedStudentId(studentId);
    setShowUploadModal(true);
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelectedStudentId(null);
  };

  const handleDeleteSubmission = studentID => {
    // Extra validation to ensure we have a valid student ID

    console.log('Handling deletion for student:', studentID); // Debug log
    setStudentToDelete(studentID);
    setShowDeleteModal(true);
  };

  const confirmDeleteSubmission = async () => {
    try {
      if (!studentToDelete) {
        return;
      }

      console.log('Sending delete request with student ID:', studentToDelete); // Debug log
      console.log(studentToDelete);
      await gradeApi.post({
        path: `/api/assignment/${assignment.id}/delete-student-submissions/`,
        body: {
          student_id: studentToDelete
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      // Trigger a refresh of the submissions list
      if (setUpdateTrigger) {
        setUpdateTrigger(prev => prev + 1);
      }

      // Close the modal
      setShowDeleteModal(false);
      setStudentToDelete(null);
      setErrorMessage('');
    } catch (error) {
      console.error('Error deleting student submissions:', error);
      setErrorMessage('Failed to delete student submissions. Please try again.');
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setStudentToDelete(null);
  };

  function process_string(input_) {
    if (!Array.isArray(input_)) {
      return '';
    }
    const filteredInput = input_.filter(item => item != null);
    return filteredInput.join(', ');
  }

  // Helper function to handle selecting/deselecting all students
  const handleSelectAllChange = event => {
    const isChecked = event.target.checked;

    // Make sure handleStudentSelectionChange is defined before calling it
    if (!handleStudentSelectionChange) {
      return;
    }

    if (isChecked) {
      // Filter submissions to get only selectable ones
      const selectableStudentIds = (submissions?.entities ?? [])
        .filter(s => s && s.first_name !== 'Unmatched Submission' && s.status !== 'Not submitted')
        .map(s => s?.id)
        .filter(id => id); // Remove any undefined IDs
      // Call the selection handler with the filtered list of IDs and true
      handleStudentSelectionChange(selectableStudentIds, true);
    } else {
      // Deselect all: call with an empty array and false
      handleStudentSelectionChange([], false);
    }
  };

  // Handler for column header clicks
  const handleHeaderClick = columnName => {
    setSortConfig(prevConfig => {
      let newDirection;
      if (prevConfig.key === columnName) {
        // Toggle direction if clicking the same column
        newDirection = prevConfig.direction === 'asc' ? 'desc' : 'asc';
      } else {
        // Default to ascending for a new column
        newDirection = 'asc';
      }

      // Update URL with new sort parameters
      const newParams = new URLSearchParams(location.search);
      newParams.set('sortKey', columnName);
      newParams.set('sortDir', newDirection);

      // Update URL without reloading the page
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });

      return {
        key: columnName,
        direction: newDirection
      };
    });
  };

  // Calculate selectable students (non-unmatched and submitted)
  const selectableStudents = React.useMemo(() => {
    // Added additional safety checks for flexible assignments
    if (!submissions || !submissions.entities) {
      return [];
    }
    return submissions.entities.filter(
      s => s && s.first_name !== 'Unmatched Submission' && s.status !== 'Not submitted'
    );
  }, [submissions?.entities]);

  // Helper function to sort raw submissions data
  const getSortedSubmissions = () => {
    if (!submissions || !submissions.entities || !Array.isArray(submissions.entities)) {
      return [];
    }

    // Make a copy of the entities to avoid mutating the original
    let sortedEntities = [...submissions.entities];

    // First sort based on the column header that was clicked
    if (sortConfig.key) {
      sortedEntities.sort((a, b) => {
        // Get values to compare based on sortConfig.key
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'First Name':
            aValue = a.first_name;
            bValue = b.first_name;
            break;
          case 'Last Name':
            aValue = a.last_name;
            bValue = b.last_name;
            break;
          case 'Date Submitted':
            // Handle null dates
            aValue = a.submission_date ? new Date(a.submission_date).getTime() : null;
            bValue = b.submission_date ? new Date(b.submission_date).getTime() : null;
            break;
          case 'Grade':
            aValue = a.grade;
            bValue = b.grade;
            break;
          default:
            return 0;
        }

        // Handle null values
        if (aValue === null && bValue === null) {
          return 0;
        }
        if (aValue === null) {
          return 1; // Null values go at the end
        }
        if (bValue === null) {
          return -1;
        }

        // Compare based on direction
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    // Then apply our priority sorting as a secondary sort
    return sortedEntities.sort((a, b) => {
      // First priority: Unmatched submissions always at the top
      const aIsUnmatched =
        a.first_name?.trim() === 'Unmatched Submission' ||
        (a.full_name && a.full_name.includes('Unmatched Submission'));
      const bIsUnmatched =
        b.first_name?.trim() === 'Unmatched Submission' ||
        (b.full_name && b.full_name.includes('Unmatched Submission'));

      if (aIsUnmatched && !bIsUnmatched) {
        return -1; // a goes before b
      }
      if (!aIsUnmatched && bIsUnmatched) {
        return 1; // a goes after b
      }

      // Second priority: Not submitted always at the bottom
      if (a.status === 'Not submitted' && b.status !== 'Not submitted') {
        return 1; // a goes after b
      }
      if (a.status !== 'Not submitted' && b.status === 'Not submitted') {
        return -1; // a goes before b
      }

      // Both are the same priority, keep original order from the previous sort
      return 0;
    });
  };

  // Force a re-render when sort config changes
  React.useEffect(() => {
    // This effect doesn't need to do anything other than trigger a re-render
  }, [sortConfig]);

  const selectableIds = selectableStudents?.map(s => s?.id) || [];
  const isAllSelectableSelected =
    selectableIds.length > 0 &&
    selectedStudents &&
    selectableIds.every(id => id && selectedStudents.includes(id));
  const isSomeSelectableSelected =
    selectedStudents && selectedStudents.length > 0 && !isAllSelectableSelected;

  // Define base column names
  const baseColumnNames = ['First Name', 'Last Name', 'Date Submitted', 'Grade'];

  // Get sort indicator for column headers (similar to RegradeRequests)
  const getSortIndicator = key => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // Create column headers with sort indicators using the RegradeRequests styling
  const columnHeadersWithSorting = baseColumnNames.map(name => (
    <div
      key={name}
      className="py-3 text-left text-gray-500 font-medium text-sm cursor-pointer hover:text-amber-500 transition-colors duration-200"
      onClick={() => handleHeaderClick(name)}
    >
      {name}
      {getSortIndicator(name)}
    </div>
  ));

  // Prepend the select-all checkbox header if in release mode
  const columnNames = isReleaseMode
    ? [
        <div key={'select-all'} className="flex justify-center">
          <input
            type="checkbox"
            className="form-checkbox h-6 w-6 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
            checked={isAllSelectableSelected}
            title="Select/Deselect All Eligible Students"
            disabled={selectableIds.length === 0}
            onChange={handleSelectAllChange}
            // Set indeterminate state via ref callback
            ref={el => {
              if (el) {
                el.indeterminate = isSomeSelectableSelected;
              }
            }}
          />
        </div>,
        ...columnHeadersWithSorting
      ]
    : columnHeadersWithSorting;

  // Use our pre-sorted entities - wrapped in useMemo to ensure it updates when sort changes
  const sortedSubmissions = React.useMemo(() => {
    // Added safety check for flexible assignments
    if (!submissions || !submissions.entities) {
      return [];
    }
    return getSortedSubmissions();
  }, [submissions?.entities, sortConfig]);

  const columnBodies = sortedSubmissions.map(value => {
    // Build the URL for student submission details
    const submissionDetailUrl =
      value.status !== 'Not submitted' && value.status !== 'Grading'
        ? `/tclasses/${assignment.course_id}/tassignments/${assignment.id}/tsubmissions/${value.id}/tdetails?sortKey=${sortConfig.key}&sortDir=${sortConfig.direction}`
        : null;

    return (
      <tr
        key={value.id || `${value.studentId}-${value.email}`}
        className={`border-b transition-colors duration-150 
                ${selectedStudents && value.id && selectedStudents.includes(value.id) ? 'bg-orange-50' : 'bg-white'} 
                ${isReleaseMode && (value.first_name === 'Unmatched Submission' || (value.full_name && value.full_name.includes('Unmatched Submission')) || value.status === 'Not submitted') ? 'bg-gray-50 opacity-60' : ''}
                ${submissionDetailUrl ? 'hover:bg-gray-100 cursor-pointer' : ''}`}
        onClick={() => {
          if (submissionDetailUrl && !isReleaseMode) {
            window.location.href = submissionDetailUrl;
          }
        }}
      >
        {/* Conditionally add checkbox cell in release mode */}
        {isReleaseMode && (
          <td className="px-6 py-3 text-center" onClick={e => e.stopPropagation()}>
            <input
              disabled={
                value.first_name === 'Unmatched Submission' ||
                (value.full_name && value.full_name.includes('Unmatched Submission')) ||
                value.status === 'Not submitted'
              }
              type="checkbox"
              checked={selectedStudents && value.id && selectedStudents.includes(value.id)}
              className="form-checkbox h-5 w-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer hover:ring-2 hover:ring-orange-200 transition-all"
              onChange={e => handleStudentSelectionChange(value.id, e.target.checked)}
            />
          </td>
        )}
        {/* First name cell */}
        <td className="text-left text-gray-500 custom-16-semi px-6 py-3">
          <div className="flex items-center">
            {value.is_reviewed && <div className="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>}
            {hasRegrade(value) && (
              <div
                className={`size-9 flex items-center justify-center rounded-full transition-all duration-300`}
              >
                <img
                  src="/assets/sidebar/quote-request.png"
                  alt="Regrade Request"
                  className="size-5"
                />
              </div>
            )}
            {value.settings && (
              <div
                className="ml-1 mr-2 flex items-center justify-center group relative"
                onClick={e => e.stopPropagation()}
              >
                <span className="flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold hover:bg-gray-200 transition-colors duration-200">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </span>
                {/* Tooltip for release date */}
                {value.settings.grade_released_at && (
                  <div className="absolute left-0 -bottom-10 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                    Released: {new Date(value.settings.grade_released_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}
            <span
              className={`${
                value.first_name?.trim() === 'Unmatched Submission' ||
                (value.full_name && value.full_name.includes('Unmatched Submission'))
                  ? 'text-red-600 font-medium px-2 py-0.5 bg-red-50 border border-red-200 rounded-md shadow-sm'
                  : value.status === 'Not submitted' || value.status === 'Grading'
                    ? 'text-gray-400'
                    : 'text-black'
              }`}
            >
              {value.first_name ?? value.email}
              {assignment.is_group_task && (
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap max-w-xs overflow-hidden text-ellipsis">
                  Group: {process_string(value.team_members)}
                </div>
              )}
            </span>
          </div>
        </td>
        {/* Last name cell */}
        <td className="text-left text-gray-500 custom-16-semi px-6 py-3">
          <span
            className={`${
              value.last_name?.trim() === 'Unmatched Submission'
                ? 'text-red-600 font-medium px-2 py-0.5 bg-red-50 border border-red-200 rounded-md shadow-sm'
                : value.status === 'Not submitted' || value.status === 'Grading'
                  ? 'text-gray-400'
                  : 'text-black'
            }`}
          >
            {value.last_name ?? ''}
          </span>
        </td>
        {/* Date submitted cell */}
        <td className="text-left text-black custom-16-med px-6 py-3">
          {value.id !== null && value.id !== undefined && !!value.submission_date ? (
            <DueTimeDisplayTable
              datetimeString={value.submission_date}
              timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
            />
          ) : (
            <span className="text-gray-500">N/A</span>
          )}
        </td>
        {/* Grade cell */}
        <td className="text-left text-black custom-16-med px-6 py-3">
          {value.grade !== null && value.grade !== undefined ? (
            `${value.grade}/${assignment.max_score}`
          ) : (
            <span className="text-gray-500">N/A</span>
          )}
        </td>
        {/* Upload/Delete buttons - prevent propagation to avoid row click triggering navigation */}
        {!isReleaseMode &&
          (assignment.type === 'pdf_problem_set' || assignment.type === 'byob_pdf_problem_set') &&
          !assignment.is_group_task &&
          assignment.is_assignment_published && (
            <td
              className="text-left text-black custom-16-med px-6 py-3"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3">
                {/*<button*/}
                {/*  className="px-4 py-2 rounded-3xl border-[1px] border-black text-smallish font-medium transition-all duration-300 desktop:hover:-translate-y-[0.5px] desktop:hover:shadow bg-orange-200 hover:bg-orange-300 text-black"*/}
                {/*  onClick={() => handleUploadClick(value.studentId)}*/}
                {/*>*/}
                {/*  Upload*/}
                {/*</button>*/}
                <button
                  className="p-2 rounded-full transition-all duration-300 hover:bg-gray-100 text-red-600"
                  title="Delete all submissions for this student"
                  onClick={() => {
                    console.log('Delete clicked for:', value); // Debug log to see what's available
                    // Directly use the studentId from the value object
                    handleDeleteSubmission(value.studentId);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="size-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </td>
          )}
      </tr>
    );
  }); // Correctly close map function body and map call

  const handleChangeAllowRegrades = async () => {
    try {
      await gradeApi.patch({
        path: `/api/assignment/${assignment.id}/allow-regrades`,
        body: { allowRegrades: !allowRegrades },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      setAllowRegrades(!allowRegrades);
      setErrorMessage('');
      toast.success(
        !allowRegrades
          ? 'Regrade requests have been enabled'
          : 'Regrade requests have been disabled'
      );
    } catch (error) {
      console.error('Error updating allow regrades:', error);
      setErrorMessage('Failed to update allow regrades');
      toast.error('Failed to update regrade request setting');
    }
  };

  useEffect(() => {
    if (assignment) {
      setAllowRegrades(assignment.allow_regrade || false);
    }
  }, [assignment]);

  if (!assignment || !submissions) {
    return <Loading />;
  }

  return (
    <div id="HomeTeacher" className="flex flex-row">
      <div id="Main-Content-container" className="flex flex-col space-y-8 w-full">
        {!isReleaseMode && (
          <div className="flex flex-col w-full">
            <h2 className="text-xl font-bold mb-1">
              Step {assignment ? (assignment.type === 'essay' ? 2 : assignment.is_exam ? 4 : 2) : 2}
              : Review grades
            </h2>
            <p className="text-sm mb-4">
              After submission upload, suggested grades will be available within two minutes.
            </p>
            {/* Grade Release Settings Panel */}
            <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Grade Release Settings</h3>
              <p className="text-sm text-gray-600 mb-4">
                Configure what students will see when you release their grades:
              </p>
              <div className="space-y-2 mb-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="submissionPdf"
                    checked={releaseOptions.submissionPdf}
                    className="form-checkbox h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-400 focus:ring-offset-0 cursor-pointer hover:ring-2 hover:ring-orange-200 transition-all"
                    onChange={handleReleaseOptionChange}
                  />
                  <span className="text-sm text-gray-700">Their submission PDF</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="grades"
                    checked={releaseOptions.grades}
                    className="form-checkbox h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-400 focus:ring-offset-0 cursor-pointer hover:ring-2 hover:ring-orange-200 transition-all"
                    onChange={handleReleaseOptionChange}
                  />
                  <span className="text-sm text-gray-700">Their grades</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="feedback"
                    checked={releaseOptions.feedback}
                    className="form-checkbox h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-400 focus:ring-offset-0 cursor-pointer hover:ring-2 hover:ring-orange-200 transition-all"
                    onChange={handleReleaseOptionChange}
                  />
                  <span className="text-sm text-gray-700">Their feedback</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="allowRegrades"
                    checked={allowRegrades}
                    className="form-checkbox h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-400 focus:ring-offset-0 cursor-pointer hover:ring-2 hover:ring-orange-200 transition-all"
                    onChange={handleChangeAllowRegrades}
                  />
                  <span className="text-sm text-gray-700">Allow regrade requests</span>
                </label>
              </div>
            </div>
          </div>
        )}
        {/* Instructional text for release mode */}
        {isReleaseMode && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg shadow-sm text-amber-900 text-base font-medium max-w-3xl">
            Select the students you wish to release grades to using the checkboxes below.
          </div>
        )}

        {reviewMode === 'Default' && (
          <div className="mx-4 mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-amber-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">Important Notice</h3>
                <div className="mt-1 text-sm text-amber-700">
                  Default reviewing is an outdated feature that will NOT be functional for
                  assignments created prior to Mar 15, 2025 or after Apr 21, 2025. Use "View by
                  Student" instead.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="ml-4 bg-white">
          {errorMessage && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {errorMessage}
            </div>
          )}
          {reviewMode === 'Default' && assignment.type !== 'flexible_problem_set' ? (
            <div className="w-full rounded-xl overflow-hidden border border-gray-200">
              {/* Replace the table with our new hierarchical component */}
              <HierarchicalQuestionView
                examPaper={examPaper}
                submissions={submissions}
                handleStartGrading={handleStartGrading}
              />
            </div>
          ) : (
            <Table
              isLoading={isLoading}
              noDataTitle="No submissions found"
              totalCount={submissions?.totalCount || 0}
              itemsPerPage={Number(itemsPerPage)}
              offset={submissions?.offset || 0}
              nextOffset={submissions?.nextOffset || null}
              pageNumber={Number(pageNumber)}
              columnNames={columnNames}
              columnBodies={columnBodies || []}
              updateSearchParams={updateSearchParams}
              extraPadding={isReleaseMode} // Add extra padding if checkbox column is present
            />
          )}
          {showUploadModal && (
            <TeacherStudentUpload
              assignmentId={assignment.id}
              studentId={selectedStudentId}
              setUpdateTrigger={setUpdateTrigger}
              onClose={handleCloseModal}
            />
          )}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Delete Student Submissions</h2>
                  <button
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-300"
                    onClick={handleCloseDeleteModal}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="mb-6">
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-red-500"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800 font-semibold">
                          Warning: This action cannot be undone
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                          This will permanently delete the submission for student ID{' '}
                          {studentToDelete?.id} from this assignment, including all grades,
                          comments, and associated data.
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Are you sure you want to delete all submissions for this student?
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    className="px-10 py-2 rounded-3xl border border-black text-smallish font-medium transition-all duration-300 desktop:hover:-translate-y-[0.5px] desktop:hover:shadow bg-red-500 hover:bg-red-600 text-white"
                    onClick={confirmDeleteSubmission}
                  >
                    Delete All
                  </button>
                  <button
                    className="px-10 py-2 bg-gray-200 border border-black hover:bg-gray-300 text-black rounded-3xl transition-colors duration-300 text-smallish font-medium"
                    onClick={handleCloseDeleteModal}
                  >
                    Cancel
                  </button>
                </div>
                {errorMessage && <p className="text-red-500 mt-4">{errorMessage}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
      {showSubQuestions && selectedGroup && (
        <div className="fixed inset-0 z-50 bg-white bg-opacity-95 flex flex-col items-center justify-center">
          <div
            className={`w-full max-w-3xl mx-auto transition-all duration-500 transform ${
              animatingToSubQuestions ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
            }`}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Select a subquestion to grade</h2>
              <button
                className="p-2 rounded-full hover:bg-gray-100"
                onClick={() => {
                  setAnimatingToSubQuestions(true);
                  setTimeout(() => {
                    setShowSubQuestions(false);
                    setAnimatingToSubQuestions(false);
                  }, 400);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto p-4">
              {assignment &&
                assignment.questions &&
                assignment.questions.map((question, qIndex) => (
                  <React.Fragment key={qIndex}>
                    {question.subQuestions &&
                      question.subQuestions.map((subQuestion, sqIndex) => (
                        <div
                          key={`${qIndex}-${sqIndex}`}
                          className="bg-white border border-gray-200 hover:border-orange-300 hover:shadow-md rounded-xl p-4 cursor-pointer transition-all duration-200"
                          onClick={() => {
                            handleGroupClick(selectedGroupIndex, selectedGroup, {
                              questionId: question.id,
                              subQuestionId: subQuestion.id
                            });
                          }}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-orange-100 text-orange-800 rounded-full flex items-center justify-center font-medium">
                              {qIndex + 1}
                              {String.fromCharCode(97 + sqIndex)}
                            </div>
                            <h3 className="font-semibold text-gray-800">
                              Question {qIndex + 1}
                              {String.fromCharCode(97 + sqIndex)}
                            </h3>
                          </div>
                          <p className="text-gray-600 line-clamp-2">{subQuestion.question}</p>
                        </div>
                      ))}
                  </React.Fragment>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TGrade;
